import { Text } from '@chakra-ui/react';
import { find, propEq } from 'ramda';

import {
  FilenameWarningItem,
  ValidationErrorMessage,
} from '../../../components/file-upload-dialog/file-upload-dialog';
import {
  ValidatedFilename,
  ValidatedFilenameErrorCode,
  ValidatedFilenameErrorFragment,
  ValidatedFilenameWarningCode,
  ValidateFilenamesMutationVariables,
} from '../../../graphql/generated';
import { createFile, EnhancedFile } from '../../../utils/file/file';

type ValidatedFilenameTuple = [
  ValidatedFilename[],
  [ValidatedFilename[], ValidatedFilename[], ValidatedFilename[]]
];

type TransformationConfig = {
  validFiles: ValidatedFilename[];
  invalidFileConfig: Record<ValidatedFilenameErrorCode, ValidatedFilename[]>;
};

/**
 * @name createTupleOfValidInvalidFiles
 * @description
 * Takes an Array<ValidatedFilename> and returns a nested array of
 * valid and invalid files
 */
export const createTupleOfValidInvalidFiles = (
  validateFilenames: ValidatedFilename[]
): ValidatedFilenameTuple =>
  validateFilenames.reduce<ValidatedFilenameTuple>(
    (acc, validationResult) => {
      if (validationResult.isValid) {
        return [[...acc[0], validationResult], acc[1]];
      }

      const isFilenameLengthLimitError = !!validationResult.errors?.some(
        (x) => x.code === ValidatedFilenameErrorCode.FILENAME_LENGTH_LIMIT
      );
      const isInvalidCharacterError = !!validationResult.errors?.some(
        (x) => x.code === ValidatedFilenameErrorCode.INVALID_CHARACTERS
      );
      const isUnsupportedFilenameError = !!validationResult.errors?.some(
        (x) => x.code === ValidatedFilenameErrorCode.UNSUPPORTED_FILE_TYPE
      );



      if (isFilenameLengthLimitError) {
        return [acc[0], [[...acc[1][0], validationResult], acc[1][1], acc[1][2]]];
      }

      if (isInvalidCharacterError) {
        return [acc[0], [acc[1][0], [...acc[1][1], validationResult], acc[1][2]]];
      }

      if (isUnsupportedFilenameError) {
        return [acc[0], [acc[1][0], acc[1][1], [...acc[1][2], validationResult]]];
      }

      return acc;
    },
    [[], [[], [], []]]
  );

/**
 * @name transformFilesUsingFileValidationResult
 * @description
 * Takes an Array<EnhancedFile> and TransformationConfig and checks each file against
 * the list of valid and invalid files.
 */
export const transformFilesUsingFileValidationResult = (
  files: EnhancedFile[],
  config: TransformationConfig
): EnhancedFile[] => {
  const { validFiles, invalidFileConfig } = config;

  return files.reduce<EnhancedFile[]>((acc, file) => {
    //1 Remove files with file length error
    const hasLengthError = invalidFileConfig.FILENAME_LENGTH_LIMIT.find(
      ({ id }) => id === file.id
    );
    const hasUnsupportedFilenameError = invalidFileConfig.UNSUPPORTED_FILE_TYPE.find(
      ({ id }) => id === file.id
    );
    if (hasLengthError || hasUnsupportedFilenameError) {
      // if file has length error or is file type is not supported, don't add to accumulator
      return acc;
    }

    //2 Rename files that have invalid chars and add to accumulator
    const hasInvalidChars = invalidFileConfig.INVALID_CHARACTERS.find(
      ({ id }) => id === file.id
    );
    if (hasInvalidChars) {
      if (!hasInvalidChars.errors) throw new Error('Error renaming file');

      const [filepath, filename] = getSuggestedFilepathAndFilename(
        hasInvalidChars.errors
      );
      

      
      const renamedEnhancedFile: EnhancedFile = {
        id: file.id,
        path: filepath,
        file: createFile(file.file, filename, file.file.type),
      };

      return [...acc, renamedEnhancedFile];
    }

    //3 Check if file is in list of valid files and add it to the accumulator, if it's not already in it
    const isValidFile = validFiles.find(({ id }) => id === file.id);
    const isValidFileInAcc = acc.some(({ id }) => id === isValidFile?.id);

    if (isValidFile && !isValidFileInAcc) {
      return [...acc, file];
    }

    return acc;
  }, []);
};

/**
 * @name getSuggestedFilepathAndFilename
 * @description
 * Takes an Array<ValidatedFilenameErrorFragment> and finds errors with code
 * `INVALID_CHARACTERS`. If found, it returns the suggested file path and file name
 */
export const getSuggestedFilepathAndFilename = (
  errors: ValidatedFilenameErrorFragment[]
): [string, string] => {
  const error = find(
    propEq('code', ValidatedFilenameErrorCode.INVALID_CHARACTERS),
    errors
  );

  if (!error) throw new Error('Error getting suggested fileoath');

  const suggestedFilepath = error.suggestedFilepath as string;
  const suggestedFilename = error.suggestedFilename as string;

  return [suggestedFilepath, suggestedFilename];
};

/**
 * @name buildInvalidCharErrors
 * @description
 * Takes an Array<ValidatedFilename> and wraps each ValidatedFilename
 * in an object containing id, and invalid char message
 */
export const buildInvalidCharErrors = (
  filesWithInvalidChars: ValidatedFilename[]
): Array<ValidationErrorMessage> => {
  return filesWithInvalidChars.map((file) => ({
    id: file.id,
    message: (
      <>
        <Text as="span" fontWeight="semibold">
          {file.filepath}{' '}
        </Text>
        <Text as="span">will be renamed to </Text>
        <Text as="span" fontWeight="semibold">
          {file.errors ? getSuggestedFilepathAndFilename(file.errors)[0] : ''}
        </Text>
      </>
    ),
  }));
};

/**
 * @name buildLengthErrors
 * @description
 * Takes an Array<ValidatedFilename> and wraps each ValidatedFilename
 * in an object containing id, and invalid name length message
 */
export const buildLengthErrors = (
  filesWithLengthError: ValidatedFilename[]
): Array<ValidationErrorMessage> => {
  return filesWithLengthError.map((file) => ({
    id: file.id,
    message: (
      <>
        <Text as="span" fontWeight="semibold">
          {file.filepath}{' '}
        </Text>
        <Text as="span">will not be uploaded</Text>
      </>
    ),
  }));
};

export const buildUnsupportedFilenameErrors = (
  filesWithUnsupportedFilenameError: ValidatedFilename[]
): Array<ValidationErrorMessage> => {
  return filesWithUnsupportedFilenameError.map((file) => ({
    id: file.id,
    message: (
      <>
        <Text as="span" fontWeight="semibold">
          {file.filepath}{' '}
        </Text>
        <Text as="span">
          is not a supported file type.
          {file.filepath.split('.').pop() === 'zip' &&
            ' You can extract the zip and upload the entire directory.'}
        </Text>
      </>
    ),
  }));
};

/**
 * @name getFilesWithWarnings
 * @description
 * Returns the validated filenames that are valid (no blocking errors) but carry
 * one or more non-blocking warnings (OCT-7346).
 */
export const getFilesWithWarnings = (
  validateFilenames: ValidatedFilename[]
): ValidatedFilename[] =>
  validateFilenames.filter(
    (file) => file.isValid && !!file.warnings && file.warnings.length > 0
  );

/**
 * @name getExpectedPatterns
 * @description
 * Extracts the expected filename pattern(s) from a file's FILENAME_PATTERN_MISMATCH
 * warning. Prefers the full set (`expectedPatterns`) and falls back to the single
 * `expectedPattern`; returns an empty array when neither is present.
 */
export const getExpectedPatterns = (file: ValidatedFilename): string[] => {
  const warning = file.warnings?.find(
    (w) => w.code === ValidatedFilenameWarningCode.FILENAME_PATTERN_MISMATCH
  );

  if (!warning) return [];

  if (warning.expectedPatterns && warning.expectedPatterns.length > 0) {
    return warning.expectedPatterns;
  }

  return warning.expectedPattern ? [warning.expectedPattern] : [];
};

/** Fallback used when the backend did not supply a specific reason. */
const DEFAULT_WARNING_REASON =
  'This file does not match the expected naming convention for this data room.';

const firstCharacterOfClass = (characterClass: string): string => {
  // "xb" -> "x", "a-z" -> "a", "1-4" -> "1", "^abc" (negated) -> "x" placeholder
  const cleaned = characterClass.replace(/^\^/, '');
  if (!cleaned) return 'x';
  return cleaned.startsWith('-') ? (cleaned[1] ?? 'x') : cleaned[0];
};

/**
 * @name humanizePattern
 * @description
 * Turns an expected-filename regex into a readable *template* of an accepted name
 * so cedants don't have to read raw regex. Variable parts become labelled
 * placeholders — `<text>` for free text, `<N digits>` (or `<digits>`/`<digit>`)
 * for numbers and `<any>` for wildcards — while literal folders, words and
 * extensions are preserved as-is. Best-effort: on any parsing issue it returns
 * the original pattern unchanged.
 *
 * e.g. `^from nippon/weekly reports/\d{4} \w+-\w+/weekly_report_\d{4}_\d{2}_\d{2}\.xlsx$`
 *   -> `from nippon/weekly reports/<4 digits> <text>-<text>/weekly_report_<4 digits>_<2 digits>_<2 digits>.xlsx`
 */
export const humanizePattern = (pattern: string): string => {
  try {
    let s = pattern.trim();
    if (s.startsWith('^')) s = s.slice(1);
    if (s.endsWith('$')) s = s.slice(0, -1);

    // wildcards first (before generic escaped-dot handling)
    s = s.replaceAll(/\.[*+]/g, '<any>'); // .*  .+  -> <any>

    // escaped literals
    s = s.replaceAll(/\\\./g, '.');
    s = s.replaceAll(/\\\//g, '/');

    // digits: \d{n} -> "<N digits>" ; \d{n,m} \d+ \d* -> "<digits>" ; \d -> "<digit>"
    s = s.replaceAll(/\\d\{(\d+)\}/g, (_m, n) => {
      const count = Number(n);
      return `<${count} ${count === 1 ? 'digit' : 'digits'}>`;
    });
    s = s.replaceAll(/\\d\{\d+,\d*\}/g, '<digits>');
    s = s.replaceAll(/\\d[+*]/g, '<digits>');
    s = s.replaceAll(/\\d/g, '<digit>');

    // word chars: every \w form -> "<text>"
    s = s.replaceAll(/\\w\{\d+(?:,\d*)?\}/g, '<text>');
    s = s.replaceAll(/\\w[+*]/g, '<text>');
    s = s.replaceAll(/\\w/g, '<text>');

    // whitespace
    s = s.replaceAll(/\\s[+*]?/g, ' ');

    // character class -> first sensible char (bounded to avoid super-linear backtracking)
    s = s.replaceAll(/\[([^\]]{1,200})\]/g, (_m, cls) => firstCharacterOfClass(cls));

    // group / alternation -> first alternative (bounded to avoid super-linear backtracking)
    s = s.replaceAll(/\((?:\?:)?([^)]{0,200})\)/g, (_m, body) => String(body).split('|')[0]);

    // drop leftover quantifiers on literals
    s = s.replaceAll(/\{\d+(?:,\d*)?\}/g, '');
    s = s.replaceAll(/([^\\])[+*?]/g, '$1');

    // unescape any remaining \X -> X
    s = s.replaceAll(/\\(.)/g, '$1');

    // tidy whitespace
    s = s.replaceAll(/\s{2,}/g, ' ').trim();

    return s.length > 0 ? s : pattern;
  } catch {
    return pattern;
  }
};

/**
 * @name getWarningMessage
 * @description
 * Returns the backend-supplied reason for a file's FILENAME_PATTERN_MISMATCH warning
 * (e.g. "Expected a .xlsx file, but this file is .csv."), or null when absent.
 */
export const getWarningMessage = (file: ValidatedFilename): string | null => {
  const warning = file.warnings?.find(
    (w) => w.code === ValidatedFilenameWarningCode.FILENAME_PATTERN_MISMATCH
  );

  return warning?.message ?? null;
};

/**
 * @name getBasename
 * @description
 * Returns the final path segment (the file name) of a filepath, using either
 * forward or back slashes as the separator.
 */
export const getBasename = (filepath: string): string => {
  const segments = filepath.split(/[/\\]/);
  return segments.at(-1) ?? filepath;
};

/**
 * @name getPatternFilenameSegment
 * @description
 * Returns the file-name portion of an expected-filename regex, i.e. everything
 * after the last (unescaped) `/`. Anchors (`^`/`$`) are removed.
 * e.g. `^to nippon/collateral report/\d{4}/\w+/schedule 4 ...\.xlsm$`
 *   -> `schedule 4 ...\.xlsm`
 */
export const getPatternFilenameSegment = (pattern: string): string => {
  let s = pattern.trim();
  if (s.startsWith('^')) s = s.slice(1);
  if (s.endsWith('$')) s = s.slice(0, -1);
  // Treat escaped slashes the same as literal ones for splitting purposes.
  s = s.replaceAll(/\\\//g, '/');
  const segments = s.split('/');
  return segments.at(-1) ?? s;
};

/** Lowercased literal words in a pattern's file-name segment (placeholders removed). */
const patternLiteralWords = (patternFilenameSegment: string): string[] =>
  humanizePattern(patternFilenameSegment)
    .replaceAll(/<[^>]{0,200}>/g, ' ')
    .toLowerCase()
    .match(/[a-z0-9]+/g) ?? [];

/**
 * @name pickClosestPattern
 * @description
 * Chooses the expected pattern whose file-name portion shares the most literal
 * words with the uploaded file name, so diagnostics target the pattern the user
 * most likely intended. Returns null when there are no patterns.
 */
export const pickClosestPattern = (
  filename: string,
  patterns: string[]
): string | null => {
  if (patterns.length === 0) return null;

  const filenameWords = new Set(filename.toLowerCase().match(/[a-z0-9]+/g) ?? []);

  let best = patterns[0];
  let bestScore = -1;
  for (const pattern of patterns) {
    const words = patternLiteralWords(getPatternFilenameSegment(pattern));
    const score = words.filter((w) => filenameWords.has(w)).length;
    if (score > bestScore) {
      bestScore = score;
      best = pattern;
    }
  }
  return best;
};

/**
 * Well-known "junk" tokens people leave on file names (OS duplicate copies, "test" scratch
 * files). Each is removed when building a suggested name. Entries with a `keyword` are only
 * stripped when that word is NOT a legitimate literal in the target pattern, so we never remove
 * a token the expected name actually requires.
 */
type JunkStripper = { strip: RegExp; keyword?: string };

const JUNK_STRIPPERS: JunkStripper[] = [
  { strip: /^copy of /i, keyword: 'copy' }, // leading "Copy of "
  { strip: /[ _-]{1,16}copy(\s{0,8}\(\d{1,8}\))?$/i, keyword: 'copy' }, // trailing " - Copy" / "_copy (2)"
  { strip: /\s{0,8}\(\d{1,8}\)$/ }, // trailing " (1)" duplicate marker
  { strip: /\s{0,8}_\d{1,8}_$/ }, // sanitized duplicate artifact " _1_"
  { strip: /[ _-]{1,16}test\b/i, keyword: 'test' }, // "_Test" / " test" scratch marker
];

/**
 * @name buildSuggestedFilename
 * @description
 * Best-effort corrected file name for a mismatched upload (OCT-7346): strips junk/duplicate
 * tokens from the base name while PRESERVING the uploaded file's own extension. The suggestion
 * never swaps the extension to the pattern's expected one, because renaming the extension
 * (e.g. .txt -> .csv) would mislabel the file without converting its content. When cleanup of
 * the original name cannot improve it, falls back to the expected naming convention itself (the
 * closest pattern's file-name, humanized into a readable template) so the user is always offered
 * a proposed name to complete, rather than their original. The result is a starting point the
 * user can edit; original casing is preserved.
 */
/**
 * @name stripJunkFromBaseName
 * @description
 * Removes duplicate/junk tokens ("copy of", "_test", "(1)", trailing separators, etc.) from a
 * file's base name (the part before the extension). A junk token is kept when it is also a
 * literal word of the expected pattern, so legitimate names are not mangled. Pure helper for
 * buildSuggestedFilename — extracted to keep that function's cognitive complexity low.
 */
const stripJunkFromBaseName = (base: string, literalWords: string[]): string => {
  let cleaned = base;
  for (const { strip, keyword } of JUNK_STRIPPERS) {
    if (keyword && literalWords.includes(keyword)) continue;
    cleaned = cleaned.replace(strip, '');
  }
  return cleaned.replace(/[\s_-]{1,64}$/, '').trim();
};

export const buildSuggestedFilename = (
  filename: string,
  patterns: string[]
): string => {
  const best = pickClosestPattern(filename, patterns);
  const segment = best ? getPatternFilenameSegment(best) : null;
  const literalWords = segment ? patternLiteralWords(segment) : [];

  const dot = filename.lastIndexOf('.');
  const rawBase = dot > 0 ? filename.slice(0, dot) : filename;
  // Option A (OCT-7346): keep the uploaded file's own extension; never swap it to the pattern's
  // expected extension. Renaming the extension (e.g. .txt -> .csv) would mislabel the file
  // without changing its actual content/type.
  const ext = dot > 0 ? filename.slice(dot + 1) : '';

  const base = stripJunkFromBaseName(rawBase, literalWords);

  // Guard against having stripped the name down to nothing meaningful: if cleanup emptied the
  // base, keep the original filename; otherwise re-attach the extension when there is one.
  let rebuilt: string;
  if (base.length === 0) {
    rebuilt = filename;
  } else {
    rebuilt = ext ? `${base}.${ext}` : base;
  }

  // If cleanup produced a genuinely different (corrected) name, offer it.
  if (rebuilt.toLowerCase() !== filename.toLowerCase()) {
    return rebuilt;
  }

  // Otherwise pre-fill with the expected naming convention itself: the closest pattern's
  // file-name humanized into a readable template, so the user starts from the target name
  // instead of their original (OCT-7346).
  if (segment) {
    const template = humanizePattern(segment).trim();
    if (template.length > 0) {
      return template;
    }
  }

  return rebuilt;
};

/**
 * @name fileNameMatchesAnyPattern
 * @description
 * True when the uploaded file name (ignoring its folder) matches the file-name portion of at
 * least one expected pattern. Distinguishes a "right file, wrong folder" case (name is fine —
 * nothing to rename) from a genuine naming mismatch. Case-insensitive; fail-safe.
 */
export const fileNameMatchesAnyPattern = (
  filename: string,
  patterns: string[]
): boolean =>
  patterns.some((pattern) => {
    const segment = getPatternFilenameSegment(pattern);
    if (!segment) return false;
    try {
      return new RegExp(`^${segment}$`, 'i').test(filename);
    } catch {
      return false;
    }
  });

/**
 * @name buildFilenameWarningItems
 * @description
 * Maps each warned ValidatedFilename to a structured item the confirmation modal renders as an
 * inline rename flow (OCT-7346): a best-effort suggested name for a genuine naming mismatch, or
 * a folder-only notice when the name is already valid but the destination folder isn't expected.
 */
export const buildFilenameWarningItems = (
  filesWithWarnings: ValidatedFilename[]
): FilenameWarningItem[] =>
  filesWithWarnings.map((file) => {
    const patterns = getExpectedPatterns(file);
    const filename = getBasename(file.filepath);
    const isWrongFolderOnly = fileNameMatchesAnyPattern(filename, patterns);

    return {
      id: file.id,
      filepath: file.filepath,
      filename,
      reason: getWarningMessage(file) ?? DEFAULT_WARNING_REASON,
      kind: isWrongFolderOnly ? 'folder' : 'rename',
      suggestedFilename: isWrongFolderOnly
        ? filename
        : buildSuggestedFilename(filename, patterns),
    };
  });

export const getValidateFilesMutationInput = (
  cedantId: string,
  files: EnhancedFile[],
  directory?: string
): ValidateFilenamesMutationVariables => ({
  cedant: cedantId,
  input: {
    files: files.map((enhancedFile) => {
      const id = enhancedFile.id;
      const filepath = enhancedFile.path;

      return {
        id,
        filepath,
        // Destination folder so the backend can match the full object key
        // (folder + filename) against the cedant's expected patterns (OCT-7346).
        ...(directory ? { directory } : {}),
      };
    }),
  },
});
